import * as pbi from 'powerbi-client';
import { HttpStatusCode } from 'axios';

import { useHttp } from '@/composables';

/**
 * @param {Object} deps
 * @param {import('vue').Ref} deps.pdfData
 * @param {boolean} deps.isPrintMode
 * @param {import('vue').ComputedRef<string>} deps.municipalitySettingsPrefix
 * @param {import('vue').Ref} deps.settingsDictionary
 * @param {import('vue').Ref} deps.error
 */
export function createPowerBiLoader({ pdfData, isPrintMode, municipalitySettingsPrefix, settingsDictionary, error }) {
  let powerbiInstance = null;

  const getSettingValue = (key) => {
    if (!settingsDictionary.value[key]) return null;
    return settingsDictionary.value[key].split(',')[0].trim();
  };

  const loadPowerBiScript = () => {
    return new Promise((resolve, reject) => {
      if (window.powerbi) {
        resolve();
        return;
      }

      const script = document.createElement('script');

      script.src = 'https://cdn.jsdelivr.net/npm/powerbi-client/dist/powerbi.min.js';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load the Power BI script.'));
      document.head.appendChild(script);
    });
  };

  const loadPowerBi = async (pagedJsContainer) => {
    const powerbiContainers = document.querySelectorAll('.pagedjs_pages .powerbi-container');

    if (powerbiContainers.length === 0) {
      console.warn('No PowerBI containers found to embed.');
      return;
    }

    const response = await useHttp().get('/profile/token');
    if (response.statusCode !== HttpStatusCode.Ok) {
      error.value = 'Failed to load pdf.';
      return;
    }
    const token = response.data;

    const reportId = getSettingValue(`${municipalitySettingsPrefix.value}-powerbi-report-id`) || token.reportId;
    const lookupColumn = getSettingValue(`${municipalitySettingsPrefix.value}-powerbi-region-lookup-selector`) || 'Municipality';
    const filterValue = pdfData.value.municipality?.powerBiRegionLookupValue;

    const reportLevelFilter = {
      $schema: 'http://powerbi.com/product/schema#basic',
      target: { table: 'Lookup', column: lookupColumn },
      operator: 'In',
      values: [filterValue],
    };

    const baseEmbedConfig = {
      type: 'report',
      id: reportId,
      embedUrl: token.embedUrl,
      accessToken: token.embedToken,
      tokenType: pbi.models.TokenType.Embed,
      settings: {
        usePhasedEmbedding: true,
        layoutType: pbi.models.LayoutType.Custom,
        customLayout: { displayOption: pbi.models.DisplayOption.FitToPage },
        panes: { filters: { expanded: false, visible: false }, pageNavigation: { visible: false } },
        background: pbi.models.BackgroundType.Transparent,
      },
      filters: [reportLevelFilter],
    };

    try {
      await loadPowerBiScript();

      powerbiInstance = new pbi.service.Service(
        pbi.factories.hpmFactory,
        pbi.factories.wpmpFactory,
        pbi.factories.routerFactory,
      );

      if (isPrintMode) {
        pagedJsContainer.classList.add('loading-powerbi-layout');
      }

      const containersArray = Array.from(powerbiContainers);
      const BATCH_SIZE = 4;

      for (let i = 0; i < containersArray.length; i += BATCH_SIZE) {
        const batch = containersArray.slice(i, i + BATCH_SIZE);

        const batchPromises = batch.map((container) => {
          return new Promise((resolve) => {
            const pageId = container.dataset.powerbiPageId;

            if (!pageId) {
              console.warn('No PowerBI Page ID found on container');
              container.innerHTML = '<div class="powerbi-error-text">Configuration Missing</div>';
              resolve(true);
              return;
            }

            const embedConfig = { ...baseEmbedConfig, pageName: pageId };

            try {
              const report = powerbiInstance.embed(container, embedConfig);

              if (isPrintMode) {
                /*
                 * Power BI fires 'rendered' once the report frame is drawn, but its data-bound
                 * visuals keep painting asynchronously afterwards. Locally the internal Docker
                 * network is fast enough that they finish before Puppeteer captures the page; on
                 * staging/production the render goes over the public URL, so without a short settle
                 * the capture grabs empty visuals. The timeout is a safety net so a missed
                 * 'rendered' event can never hang PDF generation.
                 */
                const RENDER_SETTLE_MS = 4000; // 4 seconds
                const RENDER_TIMEOUT_MS = 60000; // 1 minute

                let settled = false;
                let timeoutId;

                const finish = () => {
                  if (settled) return;
                  settled = true;
                  clearTimeout(timeoutId);
                  report.off('rendered');
                  resolve(true);
                };

                timeoutId = setTimeout(() => {
                  console.warn(`PowerBI report "${pageId}" did not signal 'rendered' in time; capturing anyway.`);
                  finish();
                }, RENDER_TIMEOUT_MS);

                report.on('rendered', () => setTimeout(finish, RENDER_SETTLE_MS));
              } else {
                resolve(true);
              }
            } catch (e) {
              console.error(`Failed to embed report ${container.id}`, e);
              resolve(true);
            }
          });
        });

        await Promise.all(batchPromises);
      }
    } catch (e) {
      console.error('PowerBI Loading Error:', e);
    } finally {
      if (isPrintMode) {
        pagedJsContainer.classList.remove('loading-powerbi-layout');
      }
    }
  };

  const cleanupPowerBi = () => {
    const containers = document.querySelectorAll('.powerbi-container');

    if (powerbiInstance && containers.length > 0) {
      for (const container of containers) {
        powerbiInstance.reset(container);
      }
    }
  };

  return { loadPowerBi, cleanupPowerBi };
}
